import { Component, inject } from '@angular/core';
import { filter, map, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { combineLatest, Observable } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import {
  AppleProfileStoreActions,
  CategoryApplicationsStoreSelectors,
  CategoryStoreSelectors,
  DeviceStoreActions,
  DeviceStoreSelectors,
} from '@era/stores';
import { AppleApplicationLockChanged, AppleDeviceDesiredNameChanged, Device, isNotUndefined } from '@rocksoft/shared';
import {
  AllowedOsUpdatesStoreActions,
  AllowedOsUpdatesStoreSelectors,
} from '../../sub-features/device-os-updates/device-allowed-os-updates-store';
import { DeviceActionBarContainerComponent } from '../device-action-bar/device-action-bar.container';
import { DeviceOsUpdateStatusContainerComponent } from '../device-os-update/device-os-update-status/device-os-update-status.container';
import { DeviceScheduledTasksContainerComponent } from '../device-scheduled-tasks/device-scheduled-tasks.container';
import { DeviceSwappedCurrentComponent } from '../device-swapped-current/device-swapped-current.component';
import { DeviceSyncStatusContainerComponent } from '../device-sync-status/device-sync-status.container';
import { AppleDeviceComponent } from './apple-device.component';
import { AppleDeviceService } from './apple-device.service';

@Component({
  selector: 'era-apple-device-current',
  templateUrl: './apple-device-current.container.html',
  imports: [
    AppleDeviceComponent,
    DeviceActionBarContainerComponent,
    DeviceOsUpdateStatusContainerComponent,
    DeviceSyncStatusContainerComponent,
    DeviceSwappedCurrentComponent,
    DeviceScheduledTasksContainerComponent,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class AppleDeviceCurrentContainerComponent {
  private readonly store = inject(Store);
  private readonly appleDeviceService = inject(AppleDeviceService);

  public readonly device$: Observable<Device>;
  public readonly isAtLatestAllowedOsVersion = this.store.selectSignal(
    AllowedOsUpdatesStoreSelectors.selectIsCurrentDeviceAtLatestAllowedOsVersion
  );
  public readonly isMdmLostModeEnabled$: Observable<boolean>;

  constructor() {
    this.device$ = this.store.select(DeviceStoreSelectors.selectDeviceByCurrentAppleDevice).pipe(
      filter(isNotUndefined),
      filter((device) => device.hasLoadedAppleDevice === true)
    );

    const category$ = this.device$.pipe(
      switchMap((device) => this.store.select(CategoryStoreSelectors.selectCategoryByDevice(device))),
      filter(isNotUndefined)
    );

    this.device$ = combineLatest([category$, this.device$]).pipe(
      switchMap(([category, device]) =>
        this.store
          .select(CategoryApplicationsStoreSelectors.selectApplicationsForDeviceWithConfiguration(category, device))
          .pipe(
            filter(isNotUndefined),
            switchMap((device) => this.appleDeviceService.observeAppleDeviceWithLockedFlag(device))
          )
      )
    );

    this.isMdmLostModeEnabled$ = this.device$.pipe(map((device) => device?.isLostModeEnabled ?? false));
  }

  public updateDevice(event: string) {
    this.store.dispatch(
      AllowedOsUpdatesStoreActions.CurrentDeviceViewActions.updateToLatestAllowedVersion({
        deviceId: event,
      })
    );
  }

  public updateDesiredName(event: AppleDeviceDesiredNameChanged) {
    this.store.dispatch(
      DeviceStoreActions.DeviceActionsActions.setDesiredName({
        deviceId: event.deviceId,
        newName: event.newDesiredName,
      })
    );
  }

  public applicationLockChanged(event: AppleApplicationLockChanged) {
    this.store.dispatch(
      AppleProfileStoreActions.AppleDeviceContainerActions.singleAppModeChanged({
        udid: event.udid,
        bundleIdentifier: event.bundleIdentifier,
        locked: event.locked,
      })
    );
  }
}
